import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type PatientGroup = {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_active: boolean;
};

type PatientGroupsContextType = {
  patientGroups: PatientGroup[];
  loading: boolean;
  fetchPatientGroups: () => Promise<void>;
};

const PatientGroupsContext = createContext<PatientGroupsContextType | undefined>(undefined);

export const PatientGroupsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const { user, organizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [patientGroups, setPatientGroups] = useState<PatientGroup[]>([]);

  const fetchPatientGroups = async () => {
    if (!user || !organizationId) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('patient_groups')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      if (data) {
        const parsedGroups: PatientGroup[] = data.map((item: any) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          color: item.color || '#3498db',
          is_active: Boolean(item.is_active),
        }));
        setPatientGroups(parsedGroups);
      } else {
        setPatientGroups([]);
      }
    } catch (error: any) {
      console.error('Error fetching patient groups:', error);
      toast({
        variant: 'destructive',
        title: 'Błąd podczas pobierania grup pacjentów',
        description: error.message || 'Nie udało się pobrać grup pacjentów',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && organizationId) {
      fetchPatientGroups();
    }
  }, [user, organizationId]);

  const value = {
    patientGroups,
    loading,
    fetchPatientGroups,
  };

  return <PatientGroupsContext.Provider value={value}>{children}</PatientGroupsContext.Provider>;
};

export const usePatientGroupsContext = () => {
  const context = useContext(PatientGroupsContext);
  if (context === undefined) {
    throw new Error('usePatientGroupsContext must be used within a PatientGroupsProvider');
  }
  return context;
};
